import { memo } from 'react'
import { animated } from '@react-spring/web'
import { useTheme } from '@nivo/theming'
import { htmlNodeTransform, htmlParentLabelTransform } from './TreemapTransitions'
import { NodeProps } from './TreeMapTypes'
import { type TreeMapPortfolioDatum } from './types'

//base code provided by Nivo: https://github.com/plouc/nivo/blob/master/packages/treemap/src/TreeMapNode.tsx

const NonMemoizedTreeMapHtmlNode = ({
    node,
    animatedProps,
    borderWidth,
    enableLabel,
    enableParentLabel,
    labelSkipSize,
}: NodeProps<TreeMapPortfolioDatum>) => {
    const theme = useTheme()

    const showLabel = enableLabel && node.isLeaf && (labelSkipSize === 0 || Math.min(node.width, node.height) > labelSkipSize)

    const showParentLabel = enableParentLabel && node.isParent

    return (
        <animated.div
            data-testid={`node.${node.data.labelStreetAndHouseNumber}`}
            id={node.path.replace(/[^\w]/gi, '-')}
            style={{
                boxSizing: 'border-box',
                position: 'absolute',
                top: 0,
                left: 0,
                transform: htmlNodeTransform(animatedProps.x, animatedProps.y),
                width: animatedProps.width,
                height: animatedProps.height,
                borderWidth,
                borderStyle: 'solid',
                borderColor: node.borderColor,
                overflow: 'hidden',
            }}
        >
            <animated.div
                style={{
                    boxSizing: 'border-box',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    opacity: node.opacity,
                    width: animatedProps.width,
                    height: animatedProps.height,
                    background: animatedProps.color,
                    borderRadius: '4px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    cursor: node.isParent ? 'default' : 'pointer',
                }}
                onMouseEnter={node.onMouseEnter}
                onMouseMove={node.onMouseMove}
                onMouseLeave={node.onMouseLeave}
                onClick={node.onClick}
            >
                {showLabel && (
                    <div>
                        {node.data.labelCityAndReference && (
                            <animated.div
                                data-testid={`label.${node.id}.city`}
                                style={{
                                    ...theme.labels.text,
                                    color: node.labelTextColor,
                                    opacity: animatedProps.labelOpacity,
                                    fontWeight: 600,
                                    pointerEvents: 'none',
                                    whiteSpace: 'nowrap',
                                    maxWidth: animatedProps.width,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    paddingLeft: '6px',
                                    paddingRight: '6px',
                                }}
                            >
                                {node.data.labelCityAndReference}
                            </animated.div>
                        )}
                        <animated.div
                            data-testid={`label.${node.id}.street`}
                            style={{
                                ...theme.labels.text,
                                color: node.labelTextColor,
                                opacity: animatedProps.labelOpacity,
                                pointerEvents: 'none',
                                whiteSpace: 'nowrap',
                                maxWidth: animatedProps.width,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                paddingLeft: '6px',
                                paddingRight: '6px',
                            }}
                        >
                            {node.data.labelStreetAndHouseNumber}
                        </animated.div>
                        <animated.div
                            data-testid={`label.${node.id}.value`}
                            style={{
                                ...theme.labels.text,
                                color: node.labelTextColor,
                                opacity: animatedProps.labelOpacity,
                                pointerEvents: 'none',
                                paddingLeft: '6px',
                                paddingRight: '6px',
                                fontWeight: 500,
                            }}
                        >
                            {node.label}
                        </animated.div>
                    </div>
                )}
            </animated.div>
            {showParentLabel && (
                <animated.span
                    data-testid={`parentLabel.${node.id}`}
                    style={{
                        ...theme.labels.text,
                        position: 'absolute',
                        display: 'flex',
                        justifyContent: 'flex-start',
                        alignItems: 'center',
                        whiteSpace: 'nowrap',
                        width: 10,
                        height: 10,
                        color: node.parentLabelTextColor,
                        transformOrigin: 'top left',
                        transform: htmlParentLabelTransform(
                            animatedProps.parentLabelX,
                            animatedProps.parentLabelY,
                            animatedProps.parentLabelRotation
                        ),
                        opacity: animatedProps.parentLabelOpacity,
                        pointerEvents: 'none',
                    }}
                >
                    {node.parentLabel}
                </animated.span>
            )}
        </animated.div>
    )
}

export const TreeMapHtmlNode = memo(NonMemoizedTreeMapHtmlNode)
